import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useFlags } from "launchdarkly-react-client-sdk";

import { usePermissions } from "components/Hook";
import GreetingHeader from "components/GreetingHeader/GreetingHeader";
import ClockInCardV2 from "components/CheckInCheckOut/ClockInCardV2";
import ClockInBlockedCard from "components/CheckInCheckOut/ClockInBlockedCard";
import CardSkeleton from "./CardSkeleton";
import PayrollBar from "components/Screens/DashBoard/EmployeeDashboardV2/PayrollHistory/PayrollBar";
import PayrollStubs from "components/Screens/DashBoard/EmployeeDashboardV2/PayrollHistory/PayrollStubs";
import EmployeePeersCom from "components/Screens/DashBoard/EmployeeDashboardV2/EmployeePeers/EmployeePeers";
import PageLoader from "components/common/PageLoader";
import Notifications from "components/Screens/DashBoard/CustomerDashBoardV2/Notifications";
import CustomButton from "components/common/Button";

import { getEmployeeDetails } from "selectors/employee";

import { GetEmployeeById } from "store/actions/EmployeePortal/GetEmployeeDetials";
import { employeeSalaryDetails } from "store/actions/Workforce/EmployeeDetils/SalaryDetails";
import { employeeSalaryInfo } from "store/actions/Workforce/EmployeeDetils/SalaryInfo";
import { getEmployeePaySlips } from "services/BambooHrTabs/salarydetails";
import { getTimesheetTicker, getEmployeeSchedule } from "services/CheckInCheckOut/CheckInAndBreaks";
import { showSnackbar } from "store/actions/Utility";

import "components/Screens/DashBoard/EmployeeDashboardV2/index.css";

const TICKER_REVALIDATE_INTERVAL_MS = 900000;
const TICKER_REVALIDATE_MIN_GAP_MS = 5000;

function EmployeeDashboardV3() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { inAppNotifications } = useFlags();
  const { loggedInUser } = usePermissions();

  const [payrollRecord, setPayrollRecord] = useState();
  const [tickerData, setTickerData] = useState(null);
  const [isLoadingTicker, setIsLoadingTicker] = useState(false);
  const [hasSchedule, setHasSchedule] = useState(null); // null = checking, true = has schedule, false = no schedule
  const [isCheckingSchedule, setIsCheckingSchedule] = useState(true);
  const lastTickerFetchRef = useRef(0);

  const userId = loggedInUser?.userId;

  const salaryInfo = useSelector((state) => state.SalaryInfo);
  const employeeDetails = useSelector(getEmployeeDetails);

  const timeoffCountry = employeeDetails?.timeoffCountry;
  const isClockInEnabledForEmployee = employeeDetails?.isClockInAllowed === true;

  const loadingEmployeeSalaryDetils = useSelector((state) => state.EmployeeSalaryDetails.loadingEmployeeSalaryDetils);
  const isLoadingEmployeeDetails = useSelector((state) => state.GetEmployeeDetials.isLoadingEmployeeDetails);
  const loadingEmployeeSalaryInfo = useSelector((state) => state.SalaryInfo.loadingEmployeeSalaryInfo);

  useEffect(() => {
    dispatch(
      employeeSalaryDetails({
        request: `employee/${loggedInUser.userId}/payroll?${new URLSearchParams({
          orderBy: JSON.stringify({
            field: "paymentDate",
            direction: "asc",
          }),
        })}`,
      })
    );

    dispatch(
      GetEmployeeById({
        employeeId: loggedInUser?.userId,
      })
    );

    if (!Object.keys(salaryInfo.employeeSalaryInfo).length) {
      dispatch(
        employeeSalaryInfo({
          request: `employee/${loggedInUser.userId}/salary`,
        })
      );
    }
    if (localStorage.payoneerCode) {
      navigate("/main/edge-wallet");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchPaySlips = async () => {
      try {
        const currentYear = new Date().getFullYear().toString();
        const data = await getEmployeePaySlips(loggedInUser?.userId, currentYear);
        if (data?.ok) {
          setPayrollRecord(data);
        }
      } catch (error) {
        dispatch(showSnackbar({ dispatch, type: "error", message: "Failed to fetch pay slips" }));
      }
    };

    fetchPaySlips();
  }, [loggedInUser?.userId, dispatch]);

  const checkScheduleExists = useCallback(async () => {
    if (!userId) {
      setIsCheckingSchedule(false);
      return;
    }

    setIsCheckingSchedule(true);
    try {
      const scheduleData = await getEmployeeSchedule(userId);
      const { schedule = [] } = scheduleData || {};
      setHasSchedule(schedule.length > 0);
    } catch (error) {
      setHasSchedule(false);
    } finally {
      setIsCheckingSchedule(false);
    }
  }, [userId]);

  const fetchTickerData = useCallback(
    async ({ silent = false } = {}) => {
      if (!userId) {
        setIsLoadingTicker(false);
        return;
      }

      if (!silent) {
        setIsLoadingTicker(true);
      }
      // Every fetch counts as a sync, so a revalidation right after a clock action is skipped.
      lastTickerFetchRef.current = Date.now();
      try {
        const tickerResponse = await getTimesheetTicker(userId);
        setTickerData(tickerResponse);
      } catch (error) {
        if (!hasSchedule) {
          setTickerData(null);
        }
      } finally {
        if (!silent) {
          setIsLoadingTicker(false);
        }
      }
    },
    [userId, hasSchedule]
  );

  useEffect(() => {
    checkScheduleExists();
  }, [checkScheduleExists]);

  useEffect(() => {
    if (hasSchedule !== null) {
      fetchTickerData();
    }
  }, [hasSchedule, fetchTickerData]);

  // The ticker tells us whether the talent is clocked in right now and which shift day that maps to.
  // It is fetched when the page opens and the work timer keeps running on the client, so a tab left
  // open (mobile browsers keep pages alive for days) would otherwise keep rendering yesterday's
  // state and offer Start Break for a day the talent has not clocked in for yet. Refresh it whenever
  // the page comes back to the foreground, and on a slow interval while it stays there.
  useEffect(() => {
    if (hasSchedule === null) return undefined;

    // Coming back to the tab fires visibilitychange and focus together, so skip a revalidation when
    // the ticker was already fetched moments ago — by the other event, or by a clock action.
    const revalidateTicker = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastTickerFetchRef.current < TICKER_REVALIDATE_MIN_GAP_MS) return;

      fetchTickerData({ silent: true });
    };

    document.addEventListener("visibilitychange", revalidateTicker);
    window.addEventListener("focus", revalidateTicker);
    // Backstop for a page that is left in the foreground and never switched away from.
    const revalidateIntervalId = setInterval(revalidateTicker, TICKER_REVALIDATE_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", revalidateTicker);
      window.removeEventListener("focus", revalidateTicker);
      clearInterval(revalidateIntervalId);
    };
  }, [hasSchedule, fetchTickerData]);

  const handleShiftConfirm = async () => {
    await checkScheduleExists();
  };

  const handleAttendanceUpdate = () => fetchTickerData();

  const handleViewTimesheet = () => {
    navigate("/main/timesheet");
  };

  const formatShiftDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const options = { year: "numeric", month: "short", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";

    let hours;
    let minutes;

    if (timeString.includes(" ")) {
      const timePart = timeString.split(" ")[1];
      [hours, minutes] = timePart.split(":").map(Number);
    } else if (timeString.includes(":")) {
      [hours, minutes] = timeString.split(":").map(Number);
    } else {
      return timeString;
    }

    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, "0");
    return `${formattedHours}:${formattedMinutes} ${ampm}`;
  };

  const shouldShowBlockedCard = () => {
    if (hasSchedule === false) return false;
    if (!tickerData) return false;

    const { todayStatus = {}, employee = {}, currentShift } = tickerData;
    const {
      isClockedIn = false,
      isClockInAllowedToday = true,
      type: statusType,
      alreadyCheckedOut = false,
      missedPreviousDayCheckout = false,
    } = todayStatus;

    if (isClockedIn) return false;

    // Don't show blocked card if there's a missed checkout to handle
    if (missedPreviousDayCheckout) return false;

    if (alreadyCheckedOut) return true;

    if (statusType === "workDay" && currentShift && employee.dateTime) {
      const { earlyStartDatetime, lateEndDatetime } = currentShift;
      const currentDateTime = new Date(employee.dateTime);
      const earlyStartDateTime = new Date(earlyStartDatetime);
      const lateEndDateTime = new Date(lateEndDatetime);

      if (currentDateTime >= earlyStartDateTime && currentDateTime <= lateEndDateTime) {
        return false;
      }

      if (currentDateTime < earlyStartDateTime) {
        return true;
      }
    }

    if (!isClockInAllowedToday) return true;

    return false;
  };

  const getBlockedCardData = () => {
    const { todayStatus, employee = {}, currentShift, upcomingShift } = tickerData;
    const statusType = todayStatus?.type;
    const { isClockInAllowedToday, alreadyCheckedOut = false } = todayStatus;

    if (alreadyCheckedOut) {
      return {
        type: "checkedOut",
        mainHeading: "Already clocked out",
        subHeading: "You have already clocked out for today",
      };
    }

    if (!isClockInAllowedToday) {
      return {
        type: "restricted",
        mainHeading: "Clock-in not available",
        subHeading: "No clock-in allowed today",
      };
    }

    if (statusType === "holiday") {
      return {
        type: "holiday",
        mainHeading: "Out of office",
        subHeading: "You are on holiday today",
      };
    }

    if (statusType && statusType !== "workDay" && statusType !== "holiday" && statusType !== "weekend") {
      const leaveName = todayStatus?.name?.toLowerCase() || "leave";
      return {
        type: "leave",
        mainHeading: "Out of office",
        subHeading: `You are out on ${leaveName} leave.`,
        leaveType: statusType,
      };
    }

    if (statusType === "weekend") {
      return {
        type: "weekend",
        mainHeading: "Enjoy your weekend",
        subHeading: "No clock-in required today",
      };
    }

    if (statusType === "workDay" && currentShift && employee.dateTime) {
      const { date: employeeDate, dateTime: employeeDateTime, timezone: employeeTimezone } = employee;

      if (!currentShift.in || !currentShift.out) {
        return {
          type: "restricted",
          mainHeading: "Clock-in not available",
          subHeading: "No shift scheduled for today",
        };
      }

      const currentDateTime = new Date(employeeDateTime);
      const earlyStartDateTime = new Date(currentShift.earlyStartDatetime);
      const startDateTime = new Date(currentShift.startDatetime);
      const endDateTime = new Date(currentShift.endDatetime);

      let heading;
      let showToday;
      let isWithinAllowedRange;

      if (currentDateTime < earlyStartDateTime) {
        heading = `Upcoming shift time on ${formatShiftDate(employeeDate)}`;
        showToday = true;
        isWithinAllowedRange = false;
      } else if (currentDateTime >= earlyStartDateTime && currentDateTime < startDateTime) {
        heading = `Shift time on ${formatShiftDate(employeeDate)}`;
        showToday = true;
        isWithinAllowedRange = true;
      } else if (currentDateTime >= startDateTime && currentDateTime <= endDateTime) {
        heading = `Shift time on ${formatShiftDate(employeeDate)}`;
        showToday = true;
        isWithinAllowedRange = true;
      } else {
        const tomorrow = new Date(employeeDate);
        tomorrow.setDate(tomorrow.getDate() + 1);
        heading = `Upcoming shift time on ${formatShiftDate(tomorrow.toISOString().split("T")[0])}`;
        showToday = false;
        isWithinAllowedRange = false;
      }

      return {
        type: "shiftRestricted",
        mainHeading: heading,
        subHeading: `${formatTime(currentShift.in)} - ${formatTime(currentShift.out)}`,
        subText: `(${employeeTimezone || "N/A"})`,
        showShiftMessage: true,
        showToday,
        isWithinAllowedRange,
      };
    }

    if (upcomingShift && employee.dateTime) {
      const { timezone: employeeTimezone } = employee;
      const { scheduleDate, in: shiftIn, out: shiftOut } = upcomingShift;

      return {
        type: "shiftRestricted",
        mainHeading: `Upcoming shift time on ${formatShiftDate(scheduleDate)}`,
        subHeading: `${formatTime(shiftIn)} - ${formatTime(shiftOut)}`,
        subText: `(${employeeTimezone || "N/A"})`,
        showShiftMessage: true,
        showToday: false,
        isWithinAllowedRange: false,
      };
    }

    return {
      type: "restricted",
      mainHeading: "Clock-in not available",
      subHeading: todayStatus?.message || "You cannot clock in at this time.",
    };
  };

  return (
    <>
      {isLoadingEmployeeDetails || loadingEmployeeSalaryDetils || loadingEmployeeSalaryInfo ? <PageLoader /> : null}
      <div className="ep-dashboard">
        <Grid
          container
          sx={{
            mt: 0,
            background: "#FFFFFF",
            width: inAppNotifications ? "100%" : "calc(100% - 328px)",
            display: "flex",
            flexDirection: "column",
            gap: "24px"
          }}>
          <GreetingHeader isEmployee />
          {isClockInEnabledForEmployee && (
            <>
              {(() => {
                if (isCheckingSchedule && hasSchedule === null) {
                  return (
                    <div className="ep-clock-in-wrapper">
                      <CardSkeleton />
                    </div>
                  );
                }
                if (hasSchedule === true && isLoadingTicker) {
                  return (
                    <div className="ep-clock-in-wrapper">
                      <CardSkeleton />
                    </div>
                  );
                }

                if (!hasSchedule) {
                  return (
                    <ClockInCardV2
                      tickerData={tickerData}
                      onShiftConfirm={handleShiftConfirm}
                      onAttendanceUpdate={handleAttendanceUpdate}
                      onRefreshTicker={fetchTickerData}
                      setTickerData={setTickerData}
                      hasSchedule={hasSchedule}
                      timeoffCountry={timeoffCountry}
                    />
                  );
                }
                if (!isCheckingSchedule) {
                  return (
                    <div>
                      {shouldShowBlockedCard() ? (
                        <div className="clock-in-card">
                          {(() => {
                            const blockedData = getBlockedCardData();
                            if (!blockedData) return null;

                            if (blockedData.type === "checkedOut") {
                              return (
                                <ClockInBlockedCard
                                  mainHeading={blockedData.mainHeading}
                                  subHeading={blockedData.subHeading}
                                  action1={{
                                    label: "View Timesheet",
                                    onClick: handleViewTimesheet,
                                    variant: "contained",
                                    color: "inherit-text",
                                    size: "medium",
                                    disabled: false,
                                  }}
                                  action2={{
                                    label: "Already clocked out",
                                    onClick: () => {},
                                    variant: "contained",
                                    color: "secondary",
                                    size: "medium",
                                    disabled: true,
                                  }}
                                />
                              );
                            }

                            if (
                              blockedData.type === "holiday" ||
                              blockedData.type === "leave" ||
                              blockedData.type === "weekend"
                            ) {
                              return (
                                <ClockInBlockedCard
                                  mainHeading={blockedData.mainHeading}
                                  subHeading={blockedData.subHeading}
                                  action2={{
                                    label: "No clock-in required",
                                    onClick: () => {},
                                    variant: "contained",
                                    color: "secondary",
                                    size: "medium",
                                    disabled: true,
                                  }}
                                />
                              );
                            }

                            if (blockedData.type === "shiftRestricted" && blockedData.showShiftMessage) {
                              return (
                                <ClockInBlockedCard
                                  mainHeading={blockedData.mainHeading}
                                  subHeading={blockedData.subHeading}
                                  subText={blockedData.subText}
                                  action1={{
                                    label: "View Timesheet",
                                    onClick: handleViewTimesheet,
                                    variant: "contained",
                                    color: "inherit-text",
                                    size: "medium",
                                    disabled: false,
                                  }}
                                  action2={{
                                    label: "No clock in allowed",
                                    onClick: () => {},
                                    disabled: true,
                                    endIcon: <InfoOutlinedIcon />,
                                    tooltip: {
                                      content:
                                        "You are allowed to clock in 3 hours before your shift starts. In case you want changes to your shift, contact HR directly.",
                                      direction: "bottom",
                                    },
                                  }}
                                />
                              );
                            }

                            return (
                              <ClockInBlockedCard
                                mainHeading={blockedData.mainHeading}
                                subHeading={blockedData.subHeading}
                                action1={{
                                  label: "View Timesheet",
                                  onClick: handleViewTimesheet,
                                  variant: "contained",
                                  color: "inherit-text",
                                  size: "medium",
                                  disabled: false,
                                }}
                                action2={{
                                  label: "Contact HR",
                                  onClick: () => {},
                                  disabled: true,
                                }}
                              />
                            );
                          })()}
                        </div>
                      ) : (
                        <ClockInCardV2
                          tickerData={tickerData}
                          onShiftConfirm={handleShiftConfirm}
                          onAttendanceUpdate={handleAttendanceUpdate}
                          onRefreshTicker={fetchTickerData}
                          setTickerData={setTickerData}
                          hasSchedule={hasSchedule}
                          timeoffCountry={timeoffCountry}
                        />
                      )}
                    </div>
                  );
                }
                // Return null if conditions not met (shouldn't happen in normal flow)
                return null;
              })()}
            </>
          )}
          <div className="ep-payroll-widget">
            <div className="ep-payroll-h1">
              <div className="heading-h4-semibold">Payroll History</div>
              <CustomButton
                label="View Details"
                variant="text"
                size="small"
                onClick={() => navigate("/main/paystubs")}
              />
            </div>
            {payrollRecord?.payload?.rows?.length > 0 && (
              <div className="ep-payroll-data-div">
                <PayrollBar payrollData={payrollRecord} />
                <PayrollStubs payrollData={payrollRecord} />
              </div>
            )}
          </div>
          <div>
            <div className="ep-payroll-h1">
              <div className="heading-h4-semibold">Peers</div>
              <CustomButton label="View All" variant="text" size="small" onClick={() => navigate("/main/peers")} />
            </div>
            <EmployeePeersCom />
          </div>
        </Grid>

        {!inAppNotifications && (
          <Grid
            sx={{
              width: "328px",
              height: "calc(100vh - 46px)",
              position: "fixed",
              background: "#F5F5FF",
              right: 0,
            }}
          >
            <Notifications isEmployee />
          </Grid>
        )}
      </div>
    </>
  );
}

export default EmployeeDashboardV3;
